import { useCallback } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { MailClient, type MailSearch } from "@/features/mail/MailClient";
import {
  listMailboxes,
  getMailAccountStatus,
  getMailPreferences,
  mailKeys,
} from "@/features/mail/mail.api";
import { applyParamUpdates } from "@/lib/searchParams";
import { hasModule, firstAllowedRoute } from "@/lib/auth";

// Module-gated (Beheer → Rechten); which mailboxes a user sees is additionally
// governed server-side by mailbox membership. mail OR mail_personal opens the
// screen — a pilot member without shared mailboxes still gets their own inbox.
export const Route = createFileRoute("/_shell/mail/")({
  beforeLoad: ({ context }) => {
    if (!hasModule(context.session, "mail") && !hasModule(context.session, "mail_personal")) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
  },
  validateSearch: (search: Record<string, unknown>): MailSearch => {
    const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
    return {
      mailbox: str(search.mailbox),
      msg: str(search.msg),
      folder: str(search.folder),
      q: str(search.q),
      scope: str(search.scope),
      tag: str(search.tag),
      connect: str(search.connect),
      view: str(search.view),
      sources: str(search.sources),
      before: str(search.before),
      sort: str(search.sort),
      order: str(search.order),
    };
  },
  component: MailScreen,
});

function MailScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { session } = Route.useRouteContext();

  const canShared = hasModule(session, "mail");
  const canPersonal = hasModule(session, "mail_personal");
  const canAi = hasModule(session, "ai");
  // Agenda module → the reading pane can turn a mail into an appointment.
  const canCalendar = hasModule(session, "calendar");
  // Taken-module → the task list beside the mail. Without it GET /tasks is a
  // 403, so the button would only ever open a panel that says it failed.
  const canTasks = hasModule(session, "tasks");
  const isAdmin = session.roles.includes("admin");

  const mailboxesQuery = useQuery({
    queryKey: mailKeys.mailboxes,
    queryFn: () => listMailboxes(),
    enabled: canShared,
  });

  // Personal-mailbox link state (mail phase 2) — drives the "Mijn postvak" tab.
  const accountQuery = useQuery({
    queryKey: mailKeys.account,
    queryFn: getMailAccountStatus,
    enabled: canPersonal,
    retry: false,
  });

  // The "Alles" working set, stored per user so it survives hot-desking.
  const preferencesQuery = useQuery({
    queryKey: mailKeys.preferences,
    queryFn: getMailPreferences,
    enabled: canShared || canPersonal,
    retry: false,
  });

  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      navigate({
        search: (prev) => applyParamUpdates(prev, updates),
        replace: true,
      });
    },
    [navigate],
  );

  return (
    <div className="wl-page wl-page--fill" data-screen-label="Back-office Mail">
      <MailClient
        mailboxes={mailboxesQuery.data ?? []}
        personal={accountQuery.data}
        personalSettled={!canPersonal || !accountQuery.isPending}
        preferences={preferencesQuery.data}
        preferencesReady={!preferencesQuery.isPending}
        userId={session.userId}
        canAi={canAi}
        canCalendar={canCalendar}
        canTasks={canTasks}
        canManageMailboxes={isAdmin}
        search={search}
        updateParams={updateParams}
      />
    </div>
  );
}
